import React, { useState } from 'react'

import { FastTooltip } from '../_shared/FastTooltip'
import { divergingLikertScaledFill, divergingLikertScaledText } from './piece-fill'
import { SegmentBar, defaultLabelMinSharePct } from '../_shared/SegmentBar'
import { ShareUnit } from '../_shared/ShareUnitToggle'
import { StatTabLabels, StatTabs } from '../_shared/StatTabs'
import { formatNpsScore } from '../kpi/formatKpiFigure'
import { DivergingLikertStoredData, DivergingLikertStoredRow } from './plot-plan'
import {
  DivergingLikertChange,
  DivergingLikertHeadlineFigure,
  DivergingLikertPieceTone,
  divergingLikertActiveView,
  divergingLikertAnchoredLabel,
  divergingLikertBaseText,
  divergingLikertChange,
  divergingLikertCountLegend,
  divergingLikertHeadline,
  divergingLikertPieceIntensities,
  divergingLikertRowScaleText,
  divergingLikertStats,
  divergingLikertTabLabels,
  divergingLikertViewLegend,
  divergingLikertViewPieces,
} from './stat-view-plan'

import { PlotStatView } from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * The app's diverging-likert plot (survey tab, cards): headline tabs
 * (Average | Top N, or Average | NPS), one bar per row, the row's headline on
 * the active view, its counts and base, and on a paired read the before and
 * after rows with the change and its printed test (decision 20, SS-1 / SS-2 /
 * P2).
 *
 * Display only: every figure, label and test name is Python's, printed at
 * its stamped decimals. The deck keeps its own slide (`RatingLikertSlide`)
 * and today's view; this component is not drawn there. Controlled or not:
 * pass `activeView` + `onViewChange`, or let it hold the view, opening on the
 * payload's `defaultView`. A payload stored before the tabs draws its one
 * stamped statistic and no tabs.
 */

export interface DivergingLikertPlotProps {
  data: DivergingLikertStoredData
  activeView?: PlotStatView | null
  onViewChange?: (view: PlotStatView) => void
  locale?: string
  /** Fill per tone; defaults to the slide tokens with the app chart tokens behind them. */
  toneColors?: Partial<Record<DivergingLikertPieceTone, string>>
  testIdPrefix?: string
  /** Tab and Average labels for a stored payload that carries none (the app's viewer-language keys). */
  tabLabelFallback?: StatTabLabels
  /** False when the caller draws one set of tabs for several plots (cut panels). Default true. */
  showTabs?: boolean
  /** False when the host draws its own key. Default true. */
  showLegend?: boolean
  /**
   * True to key a pooled NPS read by its groups' people and shares
   * ("Detractors (0-6): 12 (30%)") in place of the band legend. Read off the
   * stamped segments; any other read keeps the band legend. Default false.
   */
  showCountLegend?: boolean
  /** False when the host prints the base once for the card (the chat footnote). Default true. */
  showBase?: boolean
  /** Survey tab: print stamped counts instead of shares. Default percent. Widths stay shares. */
  shareUnit?: ShareUnit
  /**
   * The number the card header already shows. A row's base line hides only
   * when every row's base is this number and that row excluded nobody.
   */
  visibleDenominator?: number | null
  /** Survey tab passes 20. Default 32, so other callers stay as they are. */
  barHeightPx?: number
  /** Survey tab uses the 150ms tooltip. Default is the browser tooltip. */
  fastHover?: boolean
  /** Survey tab: the legend names the scale ends it holds. Default off, so stored cards and slides print today's legend. */
  showScaleEnds?: boolean
}

const DEFAULT_TONES: Record<DivergingLikertPieceTone, string> = {
  favorable: 'var(--slide-diverging-positive, hsl(var(--chart-positive)))',
  middle: 'var(--slide-neutral-mid, hsl(var(--chart-neutral)))',
  unfavorable: 'var(--slide-diverging-negative, hsl(var(--chart-negative)))',
  mixed: 'var(--slide-neutral-mid, hsl(var(--chart-neutral)))',
}

function formatNumber(value: number, decimals: number, locale?: string, signed = false): string {
  const digits = Math.max(0, decimals)
  return new Intl.NumberFormat(locale || undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    signDisplay: signed ? 'exceptZero' : 'auto',
  }).format(value)
}

function formatFigure(figure: DivergingLikertHeadlineFigure, locale?: string): string {
  if (figure.format === 'nps') {
    return formatNpsScore(figure.value, Math.max(0, figure.decimals), locale)
  }
  const number = formatNumber(figure.value, figure.decimals, locale)
  return figure.format === 'percent' ? `${number}%` : number
}

function railFigure(
  headline: ReturnType<typeof divergingLikertHeadline>,
  pieces: ReturnType<typeof divergingLikertViewPieces>,
  shareUnit: ShareUnit,
  view: PlotStatView,
  locale?: string,
): string {
  if (shareUnit === 'count' && view === 'topBoxes' && headline.primary?.format === 'percent') {
    const top = pieces.find((piece) => piece.tone === 'favorable' && piece.count != null)
    if (top?.count != null) {
      return formatNumber(top.count, 0, locale)
    }
  }
  return headline.primary ? formatFigure(headline.primary, locale) : ''
}

interface LikertHoverProps {
  isFast: boolean
  label?: string | null
  children: React.ReactElement
}

/** The survey tab's 150ms tooltip; every other host keeps the browser tooltip it had. */
function LikertHover(props: LikertHoverProps) {
  const { isFast, label, children } = props

  if (isFast) {
    return <FastTooltip label={label}>{children}</FastTooltip>
  }
  return React.cloneElement(children, { title: label ?? undefined })
}

function formatChange(change: DivergingLikertChange, data: DivergingLikertStoredData, locale?: string): string {
  const value = formatNumber(change.value, change.decimals, locale, true)
  const template = change.unit === 'pp' ? data.labels.plot_unit_pp : data.labels.signed_points
  return template ? template.replace('{v}', value) : value
}

interface LikertRowViewProps {
  row: DivergingLikertStoredRow
  data: DivergingLikertStoredData
  view: PlotStatView
  showLabel: boolean
  locale?: string
  tones: Record<DivergingLikertPieceTone, string>
  fallback: StatTabLabels
  testId: string
  showBase: boolean
  shareUnit: ShareUnit
  visibleDenominator: number | null
  barHeightPx: number
  fastHover: boolean
}

function LikertRowView(props: LikertRowViewProps) {
  const { row, data, view, showLabel, locale, tones, fallback, testId, showBase, shareUnit, visibleDenominator, barHeightPx, fastHover } = props

  const pieces = divergingLikertViewPieces(row, data, view)
  const intensities = divergingLikertPieceIntensities(pieces, (row.scale ?? data.scale)?.polarity ?? 'higher_is_better')
  const headline = divergingLikertHeadline(row, data, view, fallback)
  const baseText = divergingLikertBaseText(row, data)
  const basesMatch = visibleDenominator != null && data.rows.every((candidate) => candidate.base === visibleDenominator)
  const hideBase = basesMatch && row.excludedCount === 0
  const change = divergingLikertChange(row, view)
  // A row on its own scale (W7 Q9) names it: its ends on hover, its top beside a mean.
  const scaleText = divergingLikertRowScaleText(row)

  const changeLine = change ? (
    <div className="text-xs text-muted-foreground" data-testid={`${testId}-change`}>
      <span className="font-semibold text-foreground" data-testid={`${testId}-change-value`}>
        {formatChange(change, data, locale)}
      </span>
      {change.readLabel && <span data-testid={`${testId}-change-read`}> {change.readLabel}</span>}
      {change.testLabel && <span data-testid={`${testId}-change-test`}> ({change.testLabel})</span>}
    </div>
  ) : null

  return (
    <div className="space-y-1" data-testid={testId}>
      {showLabel && (
        <LikertHover isFast={fastHover} label={scaleText ? `${row.label} (${scaleText})` : undefined}>
          <div
            className="text-sm font-semibold text-foreground"
            data-testid={`${testId}-label`}
          >
            {row.label}
          </div>
        </LikertHover>
      )}
      <div className="flex items-center gap-4">
        {/* A zero piece keeps its place (width 0): every stamped segment is addressable. */}
        <SegmentBar
          ariaLabel={row.label}
          heightPx={barHeightPx}
          radiusPx={4}
          hover={fastHover ? 'fast' : 'native'}
          labelMinSharePct={defaultLabelMinSharePct(pieces.length)}
          rootStyle={{ flex: 1, minWidth: 0 }}
          segments={pieces.map((piece, index) => {
            const fill = divergingLikertScaledFill(tones[piece.tone], intensities[index] ?? 1)
            const printed = shareUnit === 'count' && piece.count != null
              ? formatNumber(piece.count, 0, locale)
              : `${formatNumber(piece.value, data.decimals, locale)}%`
            const countSuffix = shareUnit !== 'count' && piece.count != null ? ` (${piece.count})` : ''
            return {
              key: `${piece.label}-${index}`,
              share: piece.value,
              exactShare: piece.share,
              fill,
              text: divergingLikertScaledText(piece.tone, intensities[index] ?? 1, fill),
              printed,
              title: `${divergingLikertAnchoredLabel(piece.label, piece.anchor)}: ${printed}${countSuffix}`,
              testId: `${testId}-segment-${index}`,
            }
          })}
        />
        {/* Two lines on every view, including an empty Average. Bottom 2 stays on the segment, not a third line. */}
        <div className="w-28 shrink-0 text-right">
          <div className="flex min-h-8 items-center justify-end">
            {headline.primary && (
              <>
                <span className="text-2xl font-bold tabular-nums" data-testid={`${testId}-stat`}>
                  {railFigure(headline, pieces, shareUnit, view, locale)}
                </span>
                {headline.primary.outOf != null && (
                  <LikertHover isFast={fastHover} label={scaleText ?? undefined}>
                    <span
                      className="text-xs text-muted-foreground tabular-nums"
                      data-testid={`${testId}-stat-scale`}
                    >
                      {` /${formatNumber(headline.primary.outOf, 0, locale)}`}
                    </span>
                  </LikertHover>
                )}
              </>
            )}
          </div>
          <span className="block min-h-4 text-xs text-muted-foreground">{headline.primary?.label ?? ''}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {showBase && baseText && !hideBase && <span data-testid={`${testId}-base`}>{baseText}</span>}
        {row.lowBase && data.labels.plot_low_base && <span>{data.labels.plot_low_base}</span>}
      </div>
      {changeLine}
    </div>
  )
}

export function DivergingLikertPlot(props: DivergingLikertPlotProps) {
  const {
    data,
    activeView,
    onViewChange,
    locale,
    toneColors,
    testIdPrefix = 'diverging-likert',
    tabLabelFallback = {},
    showTabs = true,
    showLegend = true,
    showCountLegend = false,
    showBase = true,
    shareUnit = 'percent',
    visibleDenominator = null,
    barHeightPx = 32,
    fastHover = false,
    showScaleEnds = false,
  } = props

  const [heldView, setHeldView] = useState<PlotStatView | null>(null)

  const view = divergingLikertActiveView(data, activeView ?? heldView)
  const stats = divergingLikertStats(data)
  const tones = { ...DEFAULT_TONES, ...toneColors }
  const legend = data.status === 'ok' ? divergingLikertViewLegend(data, view) : []
  const countLegend = showCountLegend ? divergingLikertCountLegend(data) : null
  const showLabels = data.rows.length > 1

  const onChange = (next: PlotStatView) => {
    setHeldView(next)
    onViewChange?.(next)
  }

  if (data.status !== 'ok') {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testIdPrefix}-unsupported`}>
        {data.labels.unsupported_rating_scale}
      </p>
    )
  }

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-plot`}>
      {showTabs && stats && (
        <StatTabs
          stats={stats}
          activeView={view}
          labels={divergingLikertTabLabels(data, tabLabelFallback)}
          onViewChange={onChange}
        />
      )}
      {data.rows.map((row, index) => (
        <LikertRowView
          key={row.id}
          row={row}
          data={data}
          view={view}
          showLabel={showLabels}
          locale={locale}
          tones={tones}
          fallback={tabLabelFallback}
          testId={`${testIdPrefix}-row-${index}`}
          showBase={showBase}
          shareUnit={shareUnit}
          visibleDenominator={visibleDenominator}
          barHeightPx={barHeightPx}
          fastHover={fastHover}
        />
      ))}
      {showLegend && countLegend && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-counts`}>
          {countLegend.map((item, index) => (
            <span key={item.key} className="flex items-center gap-1" data-testid={`${testIdPrefix}-count-${index}`}>
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: tones[item.tone] }} />
              {showScaleEnds ? divergingLikertAnchoredLabel(item.label, item.anchor) : item.label}:{' '}
              <span className="font-medium text-foreground">{item.count ?? ''}</span>
              {` (${formatNumber(item.value, data.decimals, locale)}%)`}
            </span>
          ))}
        </div>
      )}
      {showLegend && !countLegend && legend.length > 0 && (
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-legend`}>
          {legend.map((item) => (
            <span key={item.key} className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: tones[item.tone] }} />
              {showScaleEnds ? divergingLikertAnchoredLabel(item.label, item.anchor) : item.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
